import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { tasksApi, regionsApi, orgApi } from '@carrent/api'
import { DueField } from '@carrent/ui'
import {
  TASK_PRIORITIES,
  priorityMeta,
  DUE_PRESETS,
  toDateTimeLocal,
  fromDateTimeLocal,
} from '@carrent/domain'
import { UserPicker } from './UserPicker'
import { TaskPicker } from './TaskPicker'

// Подписи берутся из домена. Раньше список был локальной копией
// со смайликами в тексте — бриф запрещает эмодзи как иконки,
// и копия успела разойтись с доменной.

const VIA_LABEL = {
  primary: 'менеджер региона',
  substitution: 'замещение',
  escalated_to_owner: 'эскалация владельцу',
}

const PRIORITIES = TASK_PRIORITIES.map((key) => ({ key, label: priorityMeta(key).label }))

export default function TaskCreatePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const parentIdFromUrl = params.get('parent')

  const [form, setForm] = useState({
    title: '', description: '', priority: 'normal',
    region_id: '',
  })
  const [dueAt, setDueAt]         = useState(null)
  const [assignee, setAssignee]   = useState(null)
  const [approvers, setApprovers] = useState([])
  const [parent, setParent]       = useState(null)
  const [regions, setRegions]     = useState([])
  const [routeApproval, setRouteApproval] = useState(false)
  const [responsible, setResponsible] = useState(null)
  const [err, setErr]             = useState('')
  const [loading, setLoading]     = useState(false)

  useEffect(() => {
    regionsApi.list().then(({ data }) => setRegions(data.data || []))
    // Если парент пришёл из URL — подгрузим его данные
    if (parentIdFromUrl) {
      tasksApi.get(parentIdFromUrl).then(({ data }) => {
        const p = data.data
        setParent({ id: p.id, title: p.title })
      }).catch(() => {})
    }
  }, [])

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  // Preview «кому уйдёт согласование»: резолвер оргструктуры
  // (замещение → менеджер → владелец) отвечает и здесь, и на бэкенде.
  useEffect(() => {
    if (!routeApproval || !form.region_id) { setResponsible(null); return }
    let alive = true
    orgApi.regionResponsible(form.region_id)
      .then(({ data }) => { if (alive) setResponsible(data.data || null) })
      .catch(() => { if (alive) setResponsible(null) })
    return () => { alive = false }
  }, [routeApproval, form.region_id])

  const submit = async () => {
    if (!form.title.trim()) { setErr('Введите название'); return }
    setErr(''); setLoading(true)
    try {
      const payload = {
        title:          form.title.trim(),
        description:    form.description.trim() || null,
        priority:       form.priority,
        assigned_to_id: assignee?.id || null,
        region_id:      form.region_id ? parseInt(form.region_id) : null,
        approver_ids:   approvers.map(a => a.id),
        approve_by_region_responsible: routeApproval && !!form.region_id,
        parent_id:      parent?.id || null,
        due_at:         dueAt,
      }
      const { data } = await tasksApi.create(payload)
      navigate(`/admin/tasks/${data.data.id}`, { replace: true })
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка')
    } finally { setLoading(false) }
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--gap)' }}>
        <button onClick={() => navigate(-1)} className="btn-icon">‹</button>
        <h2 style={{ margin: 0 }}>{parent ? 'Подзадача' : 'Новая задача'}</h2>
      </div>

      <div className="flex-col" style={{ gap: 'var(--gap)' }}>
        <div className="field">
          <label className="field__label">Название <span style={{color:'var(--danger)'}}>*</span></label>
          <input className="input" value={form.title} onChange={set('title')}
                 placeholder="Что нужно сделать?" />
        </div>

        <div className="field">
          <label className="field__label">Описание</label>
          <textarea className="input" rows={4}
                    value={form.description} onChange={set('description')}
                    placeholder="Подробнее..." style={{ resize: 'vertical' }} />
        </div>

        <div className="field">
          <label className="field__label">Приоритет</label>
          <select className="input" value={form.priority} onChange={set('priority')}>
            {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>

        <DueField
          value={dueAt}
          onChange={setDueAt}
          presets={DUE_PRESETS}
          toInputValue={toDateTimeLocal}
          fromInputValue={fromDateTimeLocal}
        />

        <div className="field">
          <label className="field__label">Исполнитель</label>
          <UserPicker selected={assignee} onChange={setAssignee} placeholder="Выбрать исполнителя..." />
        </div>

        <div className="field">
          <label className="field__label">Регион видимости</label>
          <select className="input" value={form.region_id} onChange={set('region_id')}>
            <option value="">Все регионы</option>
            {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        <div className="field">
          <label className="field__label">Входит в задачу</label>
          <TaskPicker selected={parent} onChange={setParent}
                      placeholder="Найти родительскую задачу..." />
          <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
            Если задача — часть большой задачи, выберите её здесь.
          </p>
        </div>

        <div className="field">
          <label className="field__label">Согласующие ({approvers.length})</label>
          <UserPicker multi selected={approvers} onChange={setApprovers}
            placeholder="Добавить согласующего..." role="manager" />
          <label className="task-route-approval">
            <input type="checkbox" checked={routeApproval}
              disabled={!form.region_id}
              onChange={e => setRouteApproval(e.target.checked)} />
            Согласует ответственный за регион
          </label>
          {!form.region_id && (
            <p className="text-muted task-route-approval__hint">
              Доступно при выбранном регионе видимости.
            </p>
          )}
          {routeApproval && responsible?.user && (
            <p className="task-route-approval__preview">
              Согласует: <b>{responsible.user.full_name}</b>
              {' '}({VIA_LABEL[responsible.via] || responsible.via})
            </p>
          )}
        </div>

        {err && <div className="alert alert--error">{err}</div>}

        <button className="btn btn--primary" onClick={submit} disabled={loading}>
          {loading ? <span className="spinner"/> : 'Создать задачу'}
        </button>
      </div>
    </div>
  )
}
